// Pure helpers (no imports) so they can be unit tested with `node --test`.

type WithStatus = { status?: unknown; detail?: unknown }

export interface RowError {
  row: number
  field: string
  message: string
}

export interface FormErrors {
  message?: string
  fields: Record<string, string>
  rows: RowError[]
}

export function isForbidden(err: unknown): boolean {
  return (err as WithStatus | null)?.status === 403
}

/**
 * Readable message for a failed response.
 * String detail is used as-is. A FastAPI 422 list becomes "field: msg" joined by "; ".
 * A non-JSON or empty body falls back to "Request failed with status N".
 */
export function apiErrorMessage(status: number, detail: unknown, bodyMessage?: string): string {
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail)) {
    const parts = detail.flatMap((item) => {
      if (!item || typeof item !== 'object') return []
      const rec = item as { loc?: unknown[]; msg?: unknown }
      const loc = Array.isArray(rec.loc) ? rec.loc : []
      const field = loc.length ? String(loc[loc.length - 1]) : ''
      const msg = typeof rec.msg === 'string' && rec.msg ? rec.msg : 'Invalid value'
      return [field ? `${field}: ${msg}` : msg]
    })
    if (parts.length) return parts.join('; ')
  }
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string' && message) return message
  }
  if (bodyMessage) return bodyMessage
  return `Request failed with status ${status}`
}

/** Errors whose field name is not one of `known`, so the form can still show them. */
export function unmatchedFieldErrors(err: unknown, known: string[]): string[] {
  return Object.entries(fieldErrors(err).fields)
    .filter(([field]) => !known.includes(field))
    .map(([field, msg]) => `${field}: ${msg}`)
}

export function isClientError(err: unknown): boolean {
  const s = (err as WithStatus | null)?.status
  return typeof s === 'number' && s >= 400 && s < 500
}

/**
 * Normalise an ApiError.detail into form errors.
 * - FastAPI 422: [{loc: ['body', 'bank_name'], msg}] -> fields
 * - CSV import 422: {message, errors: [{row, field, message}]} -> message + rows
 * - anything else: plain message (string detail) or none
 */
export function fieldErrors(err: unknown): FormErrors {
  const out: FormErrors = { fields: {}, rows: [] }
  const detail = (err as WithStatus | null)?.detail
  if (typeof detail === 'string') {
    out.message = detail
  } else if (Array.isArray(detail)) {
    for (const item of detail as Array<{ loc?: unknown[]; msg?: string }>) {
      const field = String(item.loc?.[item.loc.length - 1] ?? '')
      if (field && !(field in out.fields)) out.fields[field] = item.msg ?? 'Invalid value'
    }
  } else if (detail && typeof detail === 'object') {
    const d = detail as { message?: string; errors?: RowError[] }
    out.message = d.message
    out.rows = Array.isArray(d.errors) ? d.errors : []
  }
  return out
}
